import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, Dumbbell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const pad = (n) => String(n).padStart(2, '0');

/**
 * WorkoutCalendarModal — shows a month grid with yellow dots on days the user exercised.
 * Click a day to see the workouts logged that day.
 */
export const WorkoutCalendarModal = ({ isOpen, onClose, api }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [data, setData] = useState({ dates: [], by_date: {} });
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetchMonth = async () => {
      setLoading(true);
      try {
        const res = await api().get(`/logs/calendar?year=${year}&month=${month}`);
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load workout calendar');
      }
      setLoading(false);
    };
    fetchMonth();
  }, [isOpen, year, month]); // eslint-disable-line

  const grid = useMemo(() => {
    const firstOfMonth = new Date(year, month - 1, 1);
    // weekday 0=Sunday...6=Sat → convert to Monday-based index 0=Mon..6=Sun
    const firstDow = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${year}-${pad(month)}-${pad(d)}`);
    }
    return cells;
  }, [year, month]);

  const goPrev = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else setMonth((m) => m - 1);
    setSelectedDate(null);
  };
  const goNext = () => {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else setMonth((m) => m + 1);
    setSelectedDate(null);
  };

  const activeSet = new Set(data.dates || []);
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const selectedLogs = selectedDate ? (data.by_date?.[selectedDate] || []) : [];

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-card border-border" data-testid="workout-calendar-modal">
        <DialogHeader>
          <DialogTitle className="text-white font-heading uppercase tracking-wide">Workout Calendar</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Yellow dots mark days you exercised. Tap a day for details.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mb-2">
          <Button onClick={goPrev} variant="outline" size="sm" className="border-border text-white hover:bg-white/5" data-testid="calendar-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="font-heading text-lg text-white uppercase tracking-wider">
            {MONTHS[month - 1]} {year}
          </div>
          <Button onClick={goNext} variant="outline" size="sm" className="border-border text-white hover:bg-white/5" data-testid="calendar-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground mb-1">
              {DOW.map((d) => <div key={d} className="py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map((date, i) => {
                if (!date) return <div key={`empty-${year}-${month}-${i}`} />;
                const active = activeSet.has(date);
                const isToday = date === todayStr;
                const isSelected = date === selectedDate;
                const day = parseInt(date.slice(-2), 10);
                return (
                  <button
                    key={date}
                    onClick={() => setSelectedDate(active ? date : null)}
                    disabled={!active}
                    className={`
                      aspect-square rounded flex flex-col items-center justify-center text-sm
                      ${active ? 'bg-primary/20 border border-primary hover:bg-primary/30 cursor-pointer' : 'bg-black/50 border border-border text-muted-foreground'}
                      ${isSelected ? 'ring-2 ring-primary' : ''}
                      ${isToday ? 'font-heading' : ''}
                    `}
                    data-testid={`calendar-day-${date}`}
                  >
                    <span className={active ? 'text-white' : ''}>{day}</span>
                    {active && <Dumbbell className="h-2.5 w-2.5 text-primary mt-0.5" />}
                  </button>
                );
              })}
            </div>

            {selectedDate && selectedLogs.length > 0 && (
              <div className="mt-4 bg-black/60 border border-border rounded p-3 space-y-2">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">{selectedDate}</div>
                {selectedLogs.map((log, i) => (
                  <div key={`${selectedDate}-${log.id || log.workout_id || i}`} className="flex items-center justify-between text-sm">
                    <span className="text-white">{log.workout_title || 'Workout'}</span>
                    <span className="text-muted-foreground text-xs">
                      {log.duration_minutes || 0}m · {log.calories_burned || 0} cal
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 pt-3 border-t border-border text-sm text-muted-foreground">
              <span className="text-primary font-heading text-lg">{data.dates?.length || 0}</span> workout{(data.dates?.length || 0) === 1 ? '' : 's'} this month
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
